import {
  Box,
  Paper,
  Typography,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Button,
  Divider,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, Link } from "react-router-dom";
import {
  clearErrors,
  getOrderDetails,
  updateOrder,
} from "../../actions/orderAction";
import { UPDATE_ORDER_RESET } from "../../constants/orderConstants";
import TrackStepper from "../Order/TrackStepper";
import Loading from "./Loading";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import MetaData from "../Layouts/MetaData";

const ORDER_STATUSES = [
  "Processing",
  "Shipped",
  "Delivered",
  "Cancelled",
];

const UpdateOrder = () => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const params = useParams();

  const [status, setStatus] = useState("");
  const [location, setLocation] = useState("");

  const { order, error, loading } = useSelector(
    (state) => state.orderDetails
  );

  const { isUpdated, error: updateError } = useSelector(
    (state) => state.order
  );

  /* ---------------- FETCH ---------------- */
  useEffect(() => {
    dispatch(getOrderDetails(params.id));
  }, [dispatch, params.id]);

  /* ---------------- ERRORS ---------------- */
  useEffect(() => {
    if (error) {
      enqueueSnackbar(error, { variant: "error" });
      dispatch(clearErrors());
    }

    if (updateError) {
      enqueueSnackbar(updateError, { variant: "error" });
      dispatch(clearErrors());
    }

    if (isUpdated) {
      enqueueSnackbar("Order Updated Successfully", {
        variant: "success",
      });
      // Clear only the fields that were updated
      setLocation("");
      if (status) {
        setStatus("");
      }
      dispatch({ type: UPDATE_ORDER_RESET });
      // Refetch order details to get updated tracking
      dispatch(getOrderDetails(params.id));
    }
  }, [dispatch, error, updateError, isUpdated, enqueueSnackbar]);

  /* ---------------- SUBMIT ---------------- */
  const updateOrderSubmitHandler = (e) => {
    e.preventDefault();

    // Allow update if either status is changed OR location is provided (but not if delivering)
    if ((!status || status === order.orderStatus) && (!location.trim() || status === "Delivered")) return;

    const orderData = {};

    if (status && status !== order.orderStatus) {
      orderData.status = status;
    }

    if (location.trim() && status !== "Delivered") {
      orderData.location = location.trim();
    }

    dispatch(updateOrder(params.id, orderData));
  };

  if (loading) return <Loading />;

  if (!order || !order.user || !order.shippingInfo) return null;

  return (
    <>
      <MetaData title="Admin: Update Order" />

      {/* ================= PAGE WRAPPER ================= */}
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "background.default",
          p: 3,
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* ---------------- BACK BUTTON ---------------- */}
        <Link
          to="/admin/orders"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 500,
            width: "fit-content",
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 16 }} />
          Go Back
        </Link>

        {/* ================= MAIN CARD ================= */}
        <Paper
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 3,
          }}
        >
          {/* ---------- ADDRESS ---------- */}
          <Box
            sx={{
              flex: 1,
              p: 4,
              borderRight: {
                md: "1px solid",
              },
              borderColor: "divider",
            }}
          >
            <Typography variant="h6" fontWeight={600} mb={2}>
              Delivery Address
            </Typography>

            <Typography fontWeight={500}>
              {order.user.name}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {order.shippingInfo.address},{" "}
              {order.shippingInfo.city},{" "}
              {order.shippingInfo.state} -{" "}
              {order.shippingInfo.pincode}
            </Typography>

            <Typography variant="body2" color="text.secondary" mt={1}>
              {order.user.email}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {order.shippingInfo.phoneNo}
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="h6" fontWeight={600} mb={2}>
              Payment Status
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Status:
              </Typography>
              <Typography fontWeight={600}>
                {order.paymentInfo?.status === 'success' ? 'Paid' : 
                 order.paymentInfo?.status === 'pending' ? 'Pending' : 
                 'Failed'}
              </Typography>
            </Box>

            {order.paymentInfo?.message && (
              <Typography variant="body2" color="error" mt={1}>
                Message: {order.paymentInfo.message}
              </Typography>
            )}

            {order.paidAt && (
              <Typography variant="body2" color="text.secondary" mt={1}>
                Paid At: {new Date(order.paidAt).toLocaleString()}
              </Typography>
            )}
          </Box>

          {/* ---------- UPDATE FORM ---------- */}
          <Box
            component="form"
            onSubmit={updateOrderSubmitHandler}
            sx={{
              flex: 1,
              p: 4,
              display: "flex",
              flexDirection: "column",
              gap: 3,
            }}
          >
            <Typography variant="h6" fontWeight={600}>
              Update Status
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Current Status: <b>{order.orderStatus}</b>
            </Typography>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>

              <Select
                value={status || order.orderStatus}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
                sx={{
                  bgcolor: "background.paper",
                }}
              >
                {ORDER_STATUSES.map((s) => (
                  <MenuItem
                    key={s}
                    value={s}
                    disabled={s === order.orderStatus}
                  >
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Location Update (Optional)"
              placeholder="e.g., Package reached Mumbai warehouse"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              sx={{
                bgcolor: "background.paper",
              }}
              helperText={
                status === "Delivered"
                  ? "Location updates will be cleared when marking as delivered"
                  : "Add location information for tracking updates"
              }
              disabled={status === "Delivered"}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={(!status || status === order.orderStatus) && (!location.trim() || status === "Delivered")}
              sx={{
                borderRadius: 2,
                fontWeight: 600,
              }}
            >
              Update Order
            </Button>
          </Box>
        </Paper>

        {/* ================= ORDER ITEMS ================= */}
        {order.orderItems?.map((item) => {
          const { _id, image, name, price, quantity } = item;

          return (
            <Paper
              key={_id}
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                gap: 3,
                p: 3,
                borderRadius: 3,
                bgcolor: "background.paper",
                boxShadow: 2,
                transition: "0.2s",
                "&:hover": {
                  boxShadow: 6,
                },
              }}
            >
              {/* PRODUCT */}
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flex: 1,
                  alignItems: "center",
                }}
              >
                <img
                  src={image}
                  alt={name}
                  style={{
                    width: 90,
                    height: 90,
                    objectFit: "contain",
                    borderRadius: 10,
                  }}
                />

                <Box>
                  <Typography fontWeight={600}>
                    {name.length > 45
                      ? `${name.slice(0, 45)}...`
                      : name}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    Qty: {quantity}
                  </Typography>

                  <Typography variant="body2" color="text.secondary">
                    ₹{price}
                  </Typography>

                  <Typography fontWeight={700} mt={1}>
                    ₹{(quantity * price).toLocaleString()}
                  </Typography>
                </Box>
              </Box>

              {/* STEPPER */}
              <Box sx={{ flex: 1 }}>
                <TrackStepper
                  orderOn={order.createdAt}
                  shippedAt={order.shippedAt}
                  deliveredAt={order.deliveredAt}
                  tracking={order.tracking}
                  activeStep={
                    order.orderStatus === "Delivered"
                      ? 2
                      : order.orderStatus === "Shipped"
                      ? 1
                      : 0
                  }
                />
              </Box>
            </Paper>
          );
        })}
      </Box>
    </>
  );
};

export default UpdateOrder;
